import Vue from "vue";
import { API_URL } from "../config";

const PrefillService = {
    /**
     * When findComponentWithKey fails to locate a component in the config tree,
     * fall back to the FormIO runtime component definition (formioComponent.component).
     * Set to false to disable this fallback and only use the config tree lookup.
     */
    USE_FORMIO_COMPONENT_FALLBACK: true,

    /**
     * When true, skip raw setValue for select components whose display template
     * references item subfields beyond just "label" or "value",
     * e.g. {{ item.cap }}, {{ item.TOP_DESCRIZIONE }}.
     * These selects store entire objects, so raw string fill would break
     * downstream references like data.localita.COM_COMUNE_COD.
     */
    SKIP_RAW_FILL_SELECT_WITH_SUBFIELDS: true,

    /**
     * Italian provinces mapping (name <-> code)
     */
    ITALIAN_PROVINCES: {
        'AG': 'Agrigento', 'AL': 'Alessandria', 'AN': 'Ancona', 'AO': 'Aosta', 'AR': 'Arezzo',
        'AP': 'Ascoli Piceno', 'AT': 'Asti', 'AV': 'Avellino', 'BA': 'Bari', 'BT': 'Barletta-Andria-Trani',
        'BL': 'Belluno', 'BN': 'Benevento', 'BG': 'Bergamo', 'BI': 'Biella', 'BO': 'Bologna',
        'BZ': 'Bolzano', 'BS': 'Brescia', 'BR': 'Brindisi', 'CA': 'Cagliari', 'CL': 'Caltanissetta',
        'CB': 'Campobasso', 'CI': 'Carbonia-Iglesias', 'CE': 'Caserta', 'CT': 'Catania', 'CZ': 'Catanzaro',
        'CH': 'Chieti', 'CO': 'Como', 'CS': 'Cosenza', 'CR': 'Cremona', 'KR': 'Crotone',
        'CN': 'Cuneo', 'EN': 'Enna', 'FM': 'Fermo', 'FE': 'Ferrara', 'FI': 'Firenze',
        'FG': 'Foggia', 'FC': 'Forlì-Cesena', 'FR': 'Frosinone', 'GE': 'Genova', 'GO': 'Gorizia',
        'GR': 'Grosseto', 'IM': 'Imperia', 'IS': 'Isernia', 'SP': 'La Spezia', 'AQ': 'L\'Aquila',
        'LT': 'Latina', 'LE': 'Lecce', 'LC': 'Lecco', 'LI': 'Livorno', 'LO': 'Lodi',
        'LU': 'Lucca', 'MC': 'Macerata', 'MN': 'Mantova', 'MS': 'Massa-Carrara', 'MT': 'Matera',
        'ME': 'Messina', 'MI': 'Milano', 'MO': 'Modena', 'MB': 'Monza e Brianza', 'NA': 'Napoli',
        'NO': 'Novara', 'NU': 'Nuoro', 'OG': 'Ogliastra', 'OT': 'Olbia-Tempio', 'OR': 'Oristano',
        'PD': 'Padova', 'PA': 'Palermo', 'PR': 'Parma', 'PV': 'Pavia', 'PG': 'Perugia',
        'PU': 'Pesaro e Urbino', 'PE': 'Pescara', 'PC': 'Piacenza', 'PI': 'Pisa', 'PT': 'Pistoia',
        'PN': 'Pordenone', 'PZ': 'Potenza', 'PO': 'Prato', 'RG': 'Ragusa', 'RA': 'Ravenna',
        'RC': 'Reggio Calabria', 'RE': 'Reggio Emilia', 'RI': 'Rieti', 'RN': 'Rimini', 'RM': 'Roma',
        'RO': 'Rovigo', 'SA': 'Salerno', 'VS': 'Medio Campidano', 'SS': 'Sassari', 'SV': 'Savona',
        'SI': 'Siena', 'SR': 'Siracusa', 'SO': 'Sondrio', 'SU': 'Sud Sardegna', 'TA': 'Taranto',
        'TE': 'Teramo', 'TR': 'Terni', 'TO': 'Torino', 'TP': 'Trapani', 'TN': 'Trento',
        'TV': 'Treviso', 'TS': 'Trieste', 'UD': 'Udine', 'VA': 'Varese', 'VE': 'Venezia',
        'VB': 'Verbano-Cusio-Ossola', 'VC': 'Vercelli', 'VR': 'Verona', 'VV': 'Vibo Valentia',
        'VI': 'Vicenza', 'VT': 'Viterbo'
    },

    /**
     * Extract select options from form configuration
     * @param {Object} config - The form configuration object
     * @returns {Object} Object containing select options by key
     */
    extractSelectOptions(config) {
        const options = {};

        // Helper function to recursively find all select components
        const findSelectComponents = (components) => {
            if (!components || !Array.isArray(components)) return;

            components.forEach(component => {
                // Check for select, radio, selectboxes components
                if (['select', 'radio', 'selectboxes'].includes(component.type)) {
                    const key = component.key;

                    // Get static values if available
                    if (component.data && component.data.values && Array.isArray(component.data.values)) {
                        options[key] = {
                            type: component.type,
                            options: component.data.values
                        };
                    }
                    // Check if options are loaded from formio instance (for ajax-loaded options)
                    else if (window.formioinstance && window.formioinstance.formio) {
                        const formioComponent = window.formioinstance.formio.getComponent(key);
                        if (formioComponent && formioComponent.selectOptions) {
                            options[key] = {
                                type: component.type,
                                options: formioComponent.selectOptions
                            };
                        } else if (formioComponent && formioComponent.component && formioComponent.component.data && formioComponent.component.data.values) {
                            options[key] = {
                                type: component.type,
                                options: formioComponent.component.data.values
                            };
                        }
                    }
                }

                // Recursively check nested components (panels, fieldsets, columns, etc.)
                if (component.components) {
                    findSelectComponents(component.components);
                }
                if (component.columns) {
                    component.columns.forEach(column => {
                        if (column.components) {
                            findSelectComponents(column.components);
                        }
                    });
                }
            });
        };

        // Start recursion from form components
        if (config && config.form && config.form.components) {
            findSelectComponents(config.form.components);
        }

        return options;
    },
    generateJsonSchema(config) {
        const schema = {
            $schema: "http://json-schema.org/draft-07/schema#",
            type: "object",
            properties: {},
            required: []
        };

        const buildSchema = (components) => {
            if (!components || !Array.isArray(components)) return;

            components.forEach(component => {
                // Skip layout components but recurse into them
                const skipTypes = ['columns', 'htmlelement', 'panel', 'well', 'fieldset', 'button', 'tabs', 'table'];
                if (skipTypes.includes(component.type) || !component.key) {
                    if (component.components) buildSchema(component.components);
                    if (component.columns) component.columns.forEach(col => buildSchema(col.components));
                    return;
                }

                const fieldSchema = {
                    title: (component.label || component.key).replace(/<[^>]*>?/gm, ''), // Strip HTML tags
                    description: component.description || ""
                };

                // --- IMPROVED OPTION EXTRACTION ---
                let rawOptions = [];
                if (Array.isArray(component.values) && component.values.length > 0) {
                    rawOptions = component.values;
                } else if (component.data && Array.isArray(component.data.values)) {
                    rawOptions = component.data.values;
                }

                // Filter out invalid options but keep 0 or false
                let options = rawOptions.filter(v =>
                    v.value !== undefined &&
                    v.value !== null &&
                    String(v.value).trim() !== ""
                );

                // --- LOGIC FOR SELECT / RADIO / SELECTBOXES ---
                if (['select', 'radio', 'selectboxes'].includes(component.type)) {

                    // 1. SelectBoxes (Returns Object of Booleans)
                    if (component.type === 'selectboxes') {
                        fieldSchema.type = "object";
                        fieldSchema.properties = {};

                        if (options.length > 0) {
                            options.forEach(opt => {
                                fieldSchema.properties[opt.value] = {
                                    type: "boolean",
                                    title: opt.label
                                };
                            });
                            const labelMap = options.map(o => `"${o.value}"`).join(', ');
                            fieldSchema.description += (fieldSchema.description ? ' | ' : '') + `Keys: ${labelMap}`;
                        } else {
                            fieldSchema.additionalProperties = { type: "boolean" };
                        }
                    }

                    // 2. Multiple Selection (Returns Array of Strings)
                    else if (component.multiple) {
                        fieldSchema.type = "array";
                        fieldSchema.items = { type: "string" };

                        if (options.length > 0) {
                            fieldSchema.items.enum = options.map(o => String(o.value));
                            const labelMap = options.map(o => `${o.value} (${o.label})`).join(', ');
                            fieldSchema.description += (fieldSchema.description ? ' | ' : '') + `Allowed values: ${labelMap}`;
                        }
                    }

                    // 3. Single Selection (Returns String)
                    else {
                        if (options.length > 0) {
                            fieldSchema.type = "string";
                            fieldSchema.enum = options.map(o => String(o.value));
                            const labelMap = options.map(o => `${o.value} (${o.label})`).join(', ');
                            fieldSchema.description += (fieldSchema.description ? ' | ' : '') + `Allowed values: ${labelMap}`;
                        }
                        // Handle dynamic API/Resource fields
                        else if (component.dataSrc === 'url' || component.dataSrc === 'resource' || component.dataSrc === 'indexeddb') {
                            fieldSchema.type = "string";
                            // Include key name to help AI distinguish fields with same label
                            fieldSchema.description += (fieldSchema.description ? ' | ' : '') +
                                `NOTE: Search-based dropdown (key: "${component.key}"). Provide the display text to search for.`;
                        } else {
                            fieldSchema.type = "string";
                        }
                    }
                }
                // --- ADDRESS COMPONENTS ---
                else if (component.type === 'address') {
                    fieldSchema.type = "object";
                    fieldSchema.properties = {
                        address: { type: "string" },
                        numero_civico: { type: "string" },
                        city: { type: "string" },
                        province: { type: "string", description: "2-letter code" },
                        cap: { type: "string" }
                    };
                }
                // --- DATE/DAY COMPONENTS ---
                else if (component.type === 'day' || component.type === 'datetime') {
                    fieldSchema.type = "string";
                    fieldSchema.format = "date";
                    fieldSchema.description += (fieldSchema.description ? ' | ' : '') +
                        "Provide date in ISO format (YYYY-MM-DD). Example: 1960-01-13";
                }
                else if (component.type === 'number' || component.type === 'currency') {
                    fieldSchema.type = "number";
                }
                else if (component.type === 'checkbox') {
                    fieldSchema.type = "boolean";
                }
                else {
                    fieldSchema.type = "string";
                }

                // --- VALIDATIONS ---
                if (component.validate?.required) {
                    schema.required.push(component.key);
                }
                if (component.validate?.min && fieldSchema.type === 'number') fieldSchema.minimum = Number(component.validate.min);
                if (component.validate?.max && fieldSchema.type === 'number') fieldSchema.maximum = Number(component.validate.max);

                // // --- BUSINESS LOGIC OVERRIDES ---
                // if (component.key === 'ele_potenza_impegnata') {
                //     fieldSchema.enum = ["1.5","2","3","3.5","4","4.5","5","6","8","9","10","15","20","25","30","35","40","45","50","55","60","65","70"];
                // }

                schema.properties[component.key] = fieldSchema;

                // --- RECURSION ---
                if (component.components) buildSchema(component.components);
                if (component.columns) component.columns.forEach(col => buildSchema(col.components));
            });
        };

        if (config?.form?.components) buildSchema(config.form.components);

        if (schema.required.length === 0) delete schema.required;

        return schema;
    },
    extractAllFormFields(config) {
        const fields = {};
        const findAllFields = (components) => {
            if (!components || !Array.isArray(components)) return;

            components.forEach(component => {
                // Skip layout-only components with no business value
                const skipTypes = ['columns', 'htmlelement'];
                if (skipTypes.includes(component.type) || !component.key) {
                    if (component.components) findAllFields(component.components);
                    if (component.columns) component.columns.forEach(col => findAllFields(col.components));
                    return;
                }

                const fieldInfo = {
                    t: component.type,
                    l: component.label,
                };

                // 1. Shorthand Logic & Conditionals
                if (component.conditional?.when) {
                    fieldInfo.cond = { show: component.conditional.show, when: component.conditional.when, eq: component.conditional.eq };
                }
                if (component.customConditional) {
                    fieldInfo.cCond = component.customConditional.replace(/\s+/g, ' ').trim();
                }

                // 2. Only include 'Positive' Validation Rules
                const v = component.validate || {};
                const rules = {};
                if (v.required) rules.req = true;
                if (v.pattern) rules.ptrn = v.pattern;
                if (v.minLength) rules.min = v.minLength;
                if (v.maxLength) rules.max = v.maxLength;
                if (v.hasCustomLogic) rules.js = v.logicSnippet;
                if (Object.keys(rules).length > 0) fieldInfo.rules = rules;

                // 3. Smart Option Handling
                if (component.data?.values) {
                    // If it's the massive 'power' list, summarize it
                    if (component.key === 'ele_potenza_impegnata') {
                        fieldInfo.opts = "Range: 1.5 to 70";
                    } else {
                        fieldInfo.opts = component.data.values.map(v => v.label || v.value);
                    }
                } else if (component.dataSrc === 'url') {
                    fieldInfo.src = component.data.url;
                }

                if (component.defaultValue !== undefined && component.defaultValue !== "" && component.defaultValue !== null) {
                    fieldInfo.def = component.defaultValue;
                }

                fields[component.key] = fieldInfo;

                // Recurse
                if (component.components) findAllFields(component.components);
                if (component.columns) component.columns.forEach(col => findAllFields(col.components));
            });
        };

        if (config?.form?.components) findAllFields(config.form.components);
        return fields;
    },
    // extractAllFormFields(config) {
    //     const fields = {};
    //
    //     const findAllFields = (components) => {
    //         if (!components || !Array.isArray(components)) return;
    //
    //         components.forEach(component => {
    //             if (!component.key) {
    //                 if (component.components) findAllFields(component.components);
    //                 if (component.columns) component.columns.forEach(col => findAllFields(col.components));
    //                 return;
    //             }
    //
    //             // 1. Minimum Essential Metadata
    //             const fieldInfo = {
    //                 type: component.type,
    //                 label: component.label || component.key,
    //                 key: component.key
    //             };
    //
    //             // 2. Optimized Conditionals (Clean up long JSON strings)
    //             if (component.conditional && (component.conditional.when || component.conditional.json)) {
    //                 fieldInfo.conditional = {
    //                     show: component.conditional.show,
    //                     when: component.conditional.when,
    //                     eq: component.conditional.eq
    //                 };
    //             }
    //             if (component.customConditional) {
    //                 // Keep logic but trim whitespace to save tokens
    //                 fieldInfo.customConditional = component.customConditional.replace(/\s+/g, ' ').trim();
    //             }
    //
    //             // 3. Scrub Data Sources (The Token/URL Bloat)
    //             if (['select', 'radio', 'selectboxes'].includes(component.type)) {
    //                 if (component.data?.values) {
    //                     fieldInfo.options = component.data.values.map(v => ({ label: v.label, value: v.value }));
    //                 }
    //
    //                 if (component.dataSrc) {
    //                     fieldInfo.dataSrc = component.dataSrc;
    //                     if (component.dataSrc === 'url' && component.data) {
    //                         fieldInfo.dataConfig = {
    //                             url: component.data.url,
    //                             // CRITICAL: Remove the actual JWT token, just note its presence
    //                             hasAuth: !!component.data.headers?.some(h => h.key === 'Authorization'),
    //                             filter: component.data.filter
    //                         };
    //                     }
    //                 }
    //             }
    //
    //             // 4. Summarized Validation (Remove heavy JS logic, keep the "rules")
    //             if (component.validate) {
    //                 fieldInfo.validate = {
    //                     required: !!component.validate.required,
    //                     pattern: component.validate.pattern,
    //                     minLength: component.validate.minLength,
    //                     maxLength: component.validate.maxLength
    //                 };
    //
    //                 // If there is custom JS validation, just send a truncated version or a flag
    //                 if (component.validate.custom) {
    //                     fieldInfo.validate.hasCustomLogic = true;
    //                     // Optional: include first 50 chars of logic for context
    //                     fieldInfo.validate.logicSnippet = component.validate.custom.substring(0, 60) + "...";
    //                 }
    //             }
    //
    //             // 5. Contextual Helpers
    //             if (component.description) fieldInfo.description = component.description;
    //             if (component.defaultValue !== undefined) fieldInfo.defaultValue = component.defaultValue;
    //
    //             fields[component.key] = fieldInfo;
    //
    //             // Recursion
    //             if (component.components) findAllFields(component.components);
    //             if (component.columns) component.columns.forEach(col => findAllFields(col.components));
    //         });
    //     };
    //
    //     if (config?.form?.components) findAllFields(config.form.components);
    //     return fields;
    // },

    // /**
    //  * Extract all form fields including conditional ones
    //  * @param {Object} config - The form configuration object
    //  * @returns {Object} Object containing all fields metadata by key
    //  */
    // extractAllFormFields(config) {
    //     const fields = {};
    //
    //     // Helper function to recursively find all form components
    //     const findAllFields = (components) => {
    //         if (!components || !Array.isArray(components)) return;
    //
    //         components.forEach(component => {
    //             // Skip layout-only components without a key
    //             if (!component.key) {
    //                 // Still recurse into their children
    //                 if (component.components) {
    //                     findAllFields(component.components);
    //                 }
    //                 if (component.columns) {
    //                     component.columns.forEach(column => {
    //                         if (column.components) {
    //                             findAllFields(column.components);
    //                         }
    //                     });
    //                 }
    //                 return;
    //             }
    //
    //             // Store field metadata
    //             const fieldInfo = {
    //                 type: component.type,
    //                 label: component.label || component.key,
    //                 key: component.key
    //             };
    //
    //             // Include conditional logic if present
    //             if (component.conditional) {
    //                 fieldInfo.conditional = component.conditional;
    //             }
    //
    //             // Include custom conditional if present
    //             if (component.customConditional) {
    //                 fieldInfo.customConditional = component.customConditional;
    //             }
    //
    //             // Include options and data source for select-like components
    //             if (['select', 'radio', 'selectboxes'].includes(component.type)) {
    //                 if (component.data && component.data.values && Array.isArray(component.data.values)) {
    //                     fieldInfo.options = component.data.values;
    //                 }
    //
    //                 // Include data source information (URL, custom, etc.)
    //                 if (component.dataSrc) {
    //                     fieldInfo.dataSrc = component.dataSrc;
    //
    //                     // Include URL and related config if it's a URL-based select
    //                     if (component.dataSrc === 'url' && component.data) {
    //                         fieldInfo.dataConfig = {
    //                             url: component.data.url,
    //                             headers: component.data.headers,
    //                             selectValues: component.selectValues,
    //                             valueProperty: component.valueProperty,
    //                             idPath: component.idPath,
    //                             filter: component.filter,
    //                             searchField: component.searchField
    //                         };
    //                     }
    //                 }
    //             }
    //
    //             // Include validation rules
    //             if (component.validate) {
    //                 fieldInfo.validate = component.validate;
    //             }
    //
    //             // Include description/tooltip
    //             if (component.description) {
    //                 fieldInfo.description = component.description;
    //             }
    //             if (component.tooltip) {
    //                 fieldInfo.tooltip = component.tooltip;
    //             }
    //
    //             // Include placeholder
    //             if (component.placeholder) {
    //                 fieldInfo.placeholder = component.placeholder;
    //             }
    //
    //             // Include default value
    //             if (component.defaultValue !== undefined) {
    //                 fieldInfo.defaultValue = component.defaultValue;
    //             }
    //
    //             fields[component.key] = fieldInfo;
    //
    //             // Recursively check nested components (panels, fieldsets, columns, etc.)
    //             if (component.components) {
    //                 findAllFields(component.components);
    //             }
    //             if (component.columns) {
    //                 component.columns.forEach(column => {
    //                     if (column.components) {
    //                         findAllFields(column.components);
    //                     }
    //                 });
    //             }
    //         });
    //     };
    //
    //     // Start recursion from form components
    //     if (config && config.form && config.form.components) {
    //         findAllFields(config.form.components);
    //     }
    //
    //     return fields;
    // },

    /**
     * Process a prefill file upload
     * @param {File} file - The file to process
     * @param {Object} componentContext - Vue component context with submission, config, uploadDocType
     * @param {Function} onStart - Callback when processing starts
     * @param {Function} onSuccess - Callback when processing succeeds
     * @param {Function} onError - Callback when processing fails
     * @param {Function} onFillStart - Callback when filling process starts (after response received)
     */
    async processPrefillFile(file, componentContext, onStart, onSuccess, onError, onFillStart) {
        // Notify start
        if (onStart) onStart();

        try {
            var reader = new FileReader();
            reader.readAsArrayBuffer(file);

            reader.onload = async function() {
                try {
                    // Extract select options from form configuration
                    const selectOptions = PrefillService.extractSelectOptions(componentContext.config);

                    // Extract all field definitions including conditional ones
                    const allFields = PrefillService.extractAllFormFields(componentContext.config);

                    const formSchema = PrefillService.generateJsonSchema(componentContext.config);

                    // Create enhanced form fields payload with options and all field definitions
                    const formFieldsPayload = {
                        data: componentContext.submission.data,
                        selectOptions: selectOptions,
                        allFields: allFields
                    };
                    // console.log(formFieldsPayload);
                    // console.log(formSchema);

                    // Create FormData to send the file as binary
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('choice', componentContext.uploadDocType);
                    formData.append('formFields', JSON.stringify(formFieldsPayload));
                    formData.append('formSchema', JSON.stringify(formSchema));

                    // Call the webhook endpoint with 4 minute timeout
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 240000); // 4 minutes

                    let response;
                    try {
                        response = await fetch('https://toolsint.soloweb.io/webhook/1573ecc4-cfe8-4b90-9842-b1ce403fd33a', {
                            method: 'POST',
                            body: formData,
                            signal: controller.signal
                        });
                    } catch (fetchError) {
                        clearTimeout(timeoutId);
                        if (fetchError.name === 'AbortError') {
                            throw new Error('Request timeout - processing took longer than 4 minutes');
                        }
                        throw fetchError;
                    }
                    clearTimeout(timeoutId);

                    if (!response.ok) {
                        throw new Error('Upload failed');
                    }

                    const response_data = await response.json();

                    // Unwrap the data if it's nested under a "data" property
                    const data = response_data?.data || response_data;
                    console.log('Received prefill data:', data);

                    // Notify that filling is about to start
                    if (onFillStart) onFillStart();

                    // Flag to control prefill mode
                    const prefillFormDataDirectly = false;

                    // Prefill form fields with the response data
                    if (data && typeof data === 'object') {
                        if (prefillFormDataDirectly) {
                            // OLD MODE: Direct data assignment
                            Object.keys(data).forEach(key => {
                                if (componentContext.submission.data.hasOwnProperty(key) || true) {
                                    Vue.set(componentContext.submission.data, key, data[key]);
                                }
                            });

                            // Trigger form update
                            if (componentContext.submissionChanged) {
                                componentContext.submissionChanged(componentContext.submission.data);
                            }
                        } else {
                            // NEW MODE: Fill one by one, triggering FormIO events
                            await PrefillService.fillFieldsWithApiSupport(data, componentContext);
                        }

                        if (onSuccess) onSuccess();
                    }

                } catch (error) {
                    console.error('Error processing file:', error);
                    if (onError) onError(error);
                }
            };

            reader.onerror = function() {
                if (onError) onError(new Error('Error reading file'));
            };

        } catch (error) {
            console.error('Error uploading file:', error);
            if (onError) onError(error);
        }
    },

    /**
     * Fill form fields with API support for address and select components
     * @param {Object} data - Data to fill
     * @param {Object} componentContext - Vue component context with submission, config
     */
    async fillFieldsWithApiSupport(data, componentContext) {
        console.log('fillFieldsWithApiSupport called with data:', data);

        // Check if formioinstance is available
        if (!window.formioinstance || !window.formioinstance.formio) {
            console.error('FormIO instance not available, falling back to direct assignment');
            // Fallback to direct assignment
            Object.keys(data).forEach(key => {
                Vue.set(componentContext.submission.data, key, data[key]);
            });
            if (componentContext.submissionChanged) {
                componentContext.submissionChanged(componentContext.submission.data);
            }
            return;
        }

        // Helper function to find component definition by key
        const findComponentWithKey = (nestedObject, targetKey) => {
            function traverse(obj) {
                if (!obj || typeof obj !== 'object') return null;
                if (obj.key === targetKey) {
                    return obj;
                }
                if (Array.isArray(obj.components)) {
                    for (let component of obj.components) {
                        const found = traverse(component);
                        if (found) return found;
                    }
                }
                if (Array.isArray(obj.columns)) {
                    for (let component of obj.columns) {
                        const found = traverse(component);
                        if (found) return found;
                    }
                }
                return null;
            }
            return traverse(nestedObject);
        };

        // Helper function to get all field keys in form order
        const getFormFieldOrder = (nestedObject) => {
            const keys = [];
            function traverse(obj) {
                if (!obj || typeof obj !== 'object') return;

                // If this object has a key (it's a field), add it
                if (obj.key && typeof obj.key === 'string') {
                    keys.push(obj.key);
                }

                // Recursively traverse components
                if (Array.isArray(obj.components)) {
                    for (let component of obj.components) {
                        traverse(component);
                    }
                }

                // Recursively traverse columns
                if (Array.isArray(obj.columns)) {
                    for (let column of obj.columns) {
                        traverse(column);
                    }
                }
            }
            traverse(nestedObject);
            return keys;
        };

        // Get the order of fields from form configuration
        const formFieldOrder = getFormFieldOrder(componentContext.config.form);
        console.log('Form field order:', formFieldOrder);

        // Sort the data keys according to form field order
        const sortedKeys = Object.keys(data).sort((a, b) => {
            const indexA = formFieldOrder.indexOf(a);
            const indexB = formFieldOrder.indexOf(b);

            // If field not found in form, put it at the end
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
        });

        console.log('Processing fields in order:', sortedKeys);

        // Process each field sequentially in form order
        for (const key of sortedKeys) {
            const value = data[key];
            try {
                console.log(`Processing field: ${key} with value:`, value);

                // Get the FormIO component instance
                const formioComponent = window.formioinstance.formio.getComponent(key);
                if (!formioComponent) {
                    console.warn(`Component not found: ${key}, setting data directly`);
                    Vue.set(componentContext.submission.data, key, value);
                    continue;
                }

                // Get the component definition
                const componentDef = findComponentWithKey(componentContext.config.form, key)
                    || (PrefillService.USE_FORMIO_COMPONENT_FALLBACK && formioComponent.component ? formioComponent.component : null);

                // Check if this is a day/date component
                const isDayComponent = componentDef?.type === 'day' || componentDef?.type === 'datetime';

                // Check if this is an address component with Google provider
                const isAddressComponent = componentDef?.type === 'address';
                const isGoogleProvider = componentDef?.provider === 'google';

                // Check if this is a select with API data source
                const hasApiDataSource = componentDef?.data?.url;

                // Check if this is a province field (key contains "provincia")
                const isProvinceField = key.toLowerCase().includes('provincia');

                if (isDayComponent && typeof value === 'string') {
                    await PrefillService._handleDayComponent(key, value, componentDef, formioComponent);
                } else if (isAddressComponent && isGoogleProvider && typeof value === 'object' && value !== null) {
                    await PrefillService._handleGoogleAddress(key, value, data, formioComponent);
                } else if (hasApiDataSource && (typeof value === 'string' || (typeof value === 'object' && value !== null))) {
                    await PrefillService._handleApiSelect(key, value, componentDef, formioComponent, componentContext, isProvinceField);
                } else if (isProvinceField && !hasApiDataSource) {
                    await PrefillService._handleProvince(key, value, formioComponent);
                } else {
                    if (PrefillService.SKIP_RAW_FILL_SELECT_WITH_SUBFIELDS
                        && componentDef?.type === 'select'
                        && PrefillService._selectHasSubfieldTemplate(componentDef)) {
                        console.log(`Skipping raw fill for select ${key}: template references subfields`);
                    } else {
                        // Regular field - set value directly using FormIO's setValue
                        formioComponent.setValue(value);
                        console.log(`Set ${key} with value:`, value);
                    }
                }

                // Wait for FormIO to process events and update dependent fields
                // Longer delay for API-based selects to ensure API call completes
                // Province and day fields need minimal delay as they're just value mapping
                const delay = hasApiDataSource ? 800 : ((isProvinceField || isDayComponent) ? 200 : 300);
                await new Promise(resolve => setTimeout(resolve, delay));

            } catch (error) {
                console.error(`Error setting field ${key}:`, error);
                // Fallback: set data directly
                try {
                    Vue.set(componentContext.submission.data, key, value);
                } catch (fallbackError) {
                    console.error(`Fallback also failed for ${key}:`, fallbackError);
                }
            }
        }

        console.log('fillFieldsWithApiSupport completed');

        // Final submission changed to ensure all data is synced
        if (componentContext.submissionChanged) {
            componentContext.submissionChanged(componentContext.submission.data);
        }
    },

    /**
     * Handle Google Address component
     * @private
     */
    async _handleGoogleAddress(key, value, data, formioComponent) {
        console.log(`${key} is a Google address component with value:`, value);

        // Helper to safely extract string values (handle nested objects)
        const extractString = (val) => {
            if (typeof val === 'string') return val;
            if (typeof val === 'number') return String(val);
            return null;
        };

        // This is a Google Maps address field with structured data
        // Format the address string from the components
        const addressParts = [];

        // Get street address - handle multiple possible field names and formats
        let streetAddress = extractString(
            value.address || value.street || value.via || value.road ||
            value.route || value.address1
        );

        // Get civic/house number
        let civicNumber = extractString(
            value.numero_civico || value.civic_number || value.street_number ||
            value.house_number || value.number
        );

        // If no separate civic number, try to find it in related fields
        if (!civicNumber) {
            const relatedCivicKey = key.replace('indirizzo', 'numero_civico').replace('_osm', '');
            if (data[relatedCivicKey]) {
                civicNumber = extractString(data[relatedCivicKey]);
            }
        }

        // Check if civic number is already in the street address
        const hasNumberInAddress = streetAddress && /\d+/.test(streetAddress);

        // Build address string
        if (streetAddress) {
            addressParts.push(streetAddress);
        }

        if (civicNumber && !hasNumberInAddress) {
            addressParts.push(civicNumber);
        }

        // Add city
        const city = extractString(
            value.city || value.comune || value.locality || value.town
        );
        if (city) addressParts.push(city);

        // Add province/state
        const province = extractString(
            value.province || value.provincia || value.province_code ||
            value.state || value.region
        );
        if (province) addressParts.push(province);

        // Add postal code
        const postalCode = extractString(
            value.cap || value.zipcode || value.postal_code ||
            value.zip || value.postcode
        );
        if (postalCode) addressParts.push(postalCode);

        const searchString = addressParts.filter(p => p && typeof p === 'string').join(', ');
        console.log(`Formatted address search string for ${key}: "${searchString}"`);

        try {
            // Check if Google Maps is available
            if (window.google?.maps?.Geocoder) {
                // Use Places API to geocode and get place details
                const geocoder = new google.maps.Geocoder();

                // Wrap geocoding in a Promise for async/await
                const geocodeResult = await new Promise((resolve) => {
                    geocoder.geocode({
                        address: searchString,
                        region: 'IT' // Bias results to Italy
                    }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            console.log(`Geocoding succeeded for ${key}:`, results[0]);
                            resolve(results[0]);
                        } else {
                            console.warn(`Geocoding failed for ${key}: ${status}`);
                            resolve(null);
                        }
                    });
                });

                if (geocodeResult) {
                    // Create a structured address object exactly as FormIO/Google Places expects
                    const formattedAddress = {
                        address_components: geocodeResult.address_components,
                        formatted_address: geocodeResult.formatted_address,
                        geometry: {
                            location: {
                                lat: geocodeResult.geometry.location.lat(),
                                lng: geocodeResult.geometry.location.lng()
                            },
                            viewport: geocodeResult.geometry.viewport ? {
                                south: geocodeResult.geometry.viewport.getSouthWest().lat(),
                                west: geocodeResult.geometry.viewport.getSouthWest().lng(),
                                north: geocodeResult.geometry.viewport.getNorthEast().lat(),
                                east: geocodeResult.geometry.viewport.getNorthEast().lng()
                            } : undefined
                        },
                        place_id: geocodeResult.place_id,
                        types: geocodeResult.types,
                        formattedPlace: geocodeResult.formatted_address
                    };

                    // Set the value through FormIO to trigger all internal events
                    formioComponent.setValue(formattedAddress);
                    console.log(`Successfully set ${key} with geocoded address:`, formattedAddress);
                } else {
                    // Geocoding failed - try manual construction
                    await PrefillService._setManualAddress(key, {
                        streetAddress, civicNumber, hasNumberInAddress, city,
                        province, postalCode, searchString
                    }, formioComponent);
                }
            } else {
                // Google Maps not loaded
                await PrefillService._setManualAddress(key, {
                    streetAddress, civicNumber, hasNumberInAddress, city,
                    province, postalCode, searchString
                }, formioComponent);
            }
        } catch (addressError) {
            console.error(`Error processing address for ${key}:`, addressError);
            // Last resort fallback
            await PrefillService._setManualAddress(key, {
                streetAddress, civicNumber, hasNumberInAddress, city,
                province, postalCode, searchString
            }, formioComponent);
        }
    },

    /**
     * Set manual address when Google geocoding is not available
     * @private
     */
    async _setManualAddress(key, addressData, formioComponent) {
        const { streetAddress, civicNumber, hasNumberInAddress, city, province, postalCode, searchString } = addressData;

        console.warn(`Setting manual address for ${key}`);
        const manualComponents = [];

        if (civicNumber && !hasNumberInAddress && typeof civicNumber === 'string') {
            manualComponents.push({
                long_name: civicNumber,
                short_name: civicNumber,
                types: ["street_number"]
            });
        }

        if (streetAddress && typeof streetAddress === 'string') {
            manualComponents.push({
                long_name: streetAddress,
                short_name: streetAddress,
                types: ["route"]
            });
        }

        if (city && typeof city === 'string') {
            manualComponents.push({
                long_name: city,
                short_name: city,
                types: ["locality", "political"]
            });
        }

        if (province && typeof province === 'string') {
            manualComponents.push({
                long_name: province,
                short_name: province,
                types: ["administrative_area_level_2", "political"]
            });
        }

        if (postalCode && typeof postalCode === 'string') {
            manualComponents.push({
                long_name: postalCode,
                short_name: postalCode,
                types: ["postal_code"]
            });
        }

        manualComponents.push({
            long_name: "Italia",
            short_name: "IT",
            types: ["country", "political"]
        });

        const manualAddress = {
            address_components: manualComponents,
            formatted_address: searchString || 'Indirizzo non disponibile',
            formattedPlace: searchString || 'Indirizzo non disponibile'
        };

        console.log(`Setting manual address for ${key}:`, manualAddress);
        formioComponent.setValue(manualAddress);
    },

    /**
     * Check if a select component's template references item subfields
     * beyond just "label" or "value", indicating it stores entire objects.
     * @private
     */
    _selectHasSubfieldTemplate(componentDef) {
        if (!componentDef?.template) return false;
        const fieldRegex = /\{\{\s*item\.(\w+)\s*\}\}/g;
        let match;
        while ((match = fieldRegex.exec(componentDef.template)) !== null) {
            const field = match[1];
            if (field !== 'label' && field !== 'value') {
                return true;
            }
        }
        return false;
    },

    /**
     * Handle API-based select components
     * @private
     */
    async _handleApiSelect(key, value, componentDef, formioComponent, componentContext, isProvinceField = false) {
        console.log(`${key} has API data source, processing value:`, value);

        // Extract search term from value based on formio config
        let searchTerm;
        if (typeof value === 'string') {
            searchTerm = value;
        } else if (typeof value === 'object') {
            // If object, try to extract searchable string
            // Priority: label > value > other common fields
            searchTerm = value.label || value.value || value.name ||
                         value.codice_catastale || value.item_key ||
                         Object.values(value).find(v => typeof v === 'string' && v.length > 0);
        }

        if (!searchTerm) {
            console.warn(`No searchable term found for ${key}:`, value);
            if (PrefillService.SKIP_RAW_FILL_SELECT_WITH_SUBFIELDS && PrefillService._selectHasSubfieldTemplate(componentDef)) {
                console.log(`Skipping raw fill for ${key}: select has subfield template`);
            } else {
                formioComponent.setValue(value);
            }
            return;
        }

        console.log(`Searching API for ${key} with term: "${searchTerm}"`);

        // Build API URL using formio configuration
        const searchFieldName = componentDef.searchField || 'input';  // Default to 'input'
        const limit = componentDef.limit || 100;

        // Resolve template expressions in the base URL (e.g., {{ data.localita.COM_COMUNE_COD }})
        let resolvedBaseUrl = componentDef.data.url;
        if (resolvedBaseUrl.includes('{{')) {
            resolvedBaseUrl = resolvedBaseUrl.replace(/\{\{(.*?)\}\}/g, (match, expr) => {
                try {
                    let val = new Function('data', `return ${expr.trim()}`)(componentContext.submission.data);
                    return val !== undefined && val !== null ? encodeURIComponent(String(val)) : '';
                } catch (e) {
                    console.warn(`Error resolving URL template in ${key}: ${match}`, e);
                    return '';
                }
            });
        }

        const urlSeparator = resolvedBaseUrl.includes('?') ? '&' : '?';
        let url = API_URL + resolvedBaseUrl +
                 `${urlSeparator}limit=${limit}&skip=0&${searchFieldName}=` +
                 encodeURIComponent(searchTerm);

        // Apply filter if present (e.g., "swiluonas=1")
        if (componentDef.filter) {
            const evaluateTemplate = function (template, data) {
                return template.replace(/{{(.*?)}}/g, (match, expr) => {
                    try {
                        let value = new Function('data', `return ${expr}`)(data);
                        return value !== undefined ? value : match;
                    } catch (e) {
                        return match;
                    }
                });
            };
            try {
                const queryString = evaluateTemplate(componentDef.filter, componentContext.submission.data);
                url += '&' + queryString;
            } catch (e) {
                console.warn(`Error applying filter for ${key}:`, e);
            }
        }

        // Format headers from formio config
        const formattedHeaders = componentDef.data.headers?.reduce((acc, header) => {
            acc[header.key] = header.value;
            return acc;
        }, {}) || {};

        try {
            console.log(`Fetching from URL: ${url}`);

            // Fetch API results
            const response = await fetch(url, {
                headers: formattedHeaders,
                method: "GET"
            });
            const responseData = await response.json();

            // Extract results based on selectValues config (e.g., "results")
            let apiData;
            if (componentDef.selectValues && responseData[componentDef.selectValues]) {
                apiData = responseData[componentDef.selectValues];
            } else if (Array.isArray(responseData)) {
                apiData = responseData;
            } else {
                // Try common result keys
                apiData = responseData.results || responseData.data || responseData.items || [];
            }

            console.log(`API returned ${Array.isArray(apiData) ? apiData.length : 0} results for ${key}`);

            // Helper function to find exact match in results
            const findExactMatch = (data, search) => {
                if (!data || !Array.isArray(data) || !search) return null;

                const searchLower = String(search).toLowerCase().trim();

                // Try exact match on common fields
                return data.find(item => {
                    const label = String(item.label || '').toLowerCase().trim();
                    const value = String(item.value || '').toLowerCase().trim();
                    const name = String(item.name || '').toLowerCase().trim();

                    return label === searchLower || value === searchLower || name === searchLower;
                });
            };

            // Check if we have an exact match with current results
            let hasExactMatch = findExactMatch(apiData, searchTerm);

            // If no exact match and it's a province field with a code, try converting and retrying
            if (!hasExactMatch && isProvinceField && typeof searchTerm === 'string') {
                const upperValue = searchTerm.trim().toUpperCase();
                // Check if it's a 2-letter province code
                if (upperValue.length === 2 && PrefillService.ITALIAN_PROVINCES[upperValue]) {
                    const provinceName = PrefillService.ITALIAN_PROVINCES[upperValue];
                    console.log(`No exact match for "${searchTerm}", retrying with converted province name: ${provinceName}`);

                    // Retry with province name
                    let retryUrl = API_URL + componentDef.data.url +
                                    `?limit=${limit}&skip=0&${searchFieldName}=` +
                                    encodeURIComponent(provinceName);

                    // Apply filter if present (same as original request)
                    if (componentDef.filter) {
                        const evaluateTemplate = function (template, data) {
                            return template.replace(/{{(.*?)}}/g, (match, expr) => {
                                try {
                                    let value = new Function('data', `return ${expr}`)(data);
                                    return value !== undefined ? value : match;
                                } catch (e) {
                                    return match;
                                }
                            });
                        };
                        try {
                            const queryString = evaluateTemplate(componentDef.filter, componentContext.submission.data);
                            retryUrl += '&' + queryString;
                        } catch (e) {
                            console.warn(`Error applying filter for retry:`, e);
                        }
                    }

                    try {
                        const retryResponse = await fetch(retryUrl, {
                            headers: formattedHeaders,
                            method: "GET"
                        });
                        const retryData = await retryResponse.json();

                        // Extract results
                        let retryApiData;
                        if (componentDef.selectValues && retryData[componentDef.selectValues]) {
                            retryApiData = retryData[componentDef.selectValues];
                        } else if (Array.isArray(retryData)) {
                            retryApiData = retryData;
                        } else {
                            retryApiData = retryData.results || retryData.data || retryData.items || [];
                        }

                        console.log(`Retry returned ${Array.isArray(retryApiData) ? retryApiData.length : 0} results`);

                        // If retry has results, use those instead
                        if (retryApiData && Array.isArray(retryApiData) && retryApiData.length > 0) {
                            apiData = retryApiData;
                            // Update searchTerm for matching logic below
                            searchTerm = provinceName;
                        }
                    } catch (retryError) {
                        console.error(`Error retrying with province name:`, retryError);
                    }
                }
            }

            // If still no results and search term contains a street toponimo prefix, retry without it
            if ((!apiData || !Array.isArray(apiData) || apiData.length === 0) && typeof searchTerm === 'string') {
                const streetToponimi = ['VIA', 'VIALE', 'V.LE', 'PIAZZA', 'P.ZA', 'PIAZZALE', 'P.LE',
                    'LARGO', 'L.GO', 'CORSO', 'C.SO', 'VICOLO', 'V.LO', 'LUNGOMARE', 'BORGO',
                    'CONTRADA', 'C.DA', 'STRADA', 'STR.', 'TRAVERSA', 'SALITA', 'DISCESA',
                    'GALLERIA', 'PASSAGGIO', 'RAMPA', 'RIONE', 'RONCO', 'SENTIERO', 'VIOTTOLO'];
                const searchParts = searchTerm.trim().split(/\s+/);
                if (searchParts.length > 1 && streetToponimi.includes(searchParts[0].toUpperCase())) {
                    const refinedTerm = searchParts.slice(1).join(' ');
                    console.log(`No API results for "${searchTerm}", retrying without toponimo: "${refinedTerm}"`);

                    let retryUrl = API_URL + resolvedBaseUrl +
                                    `${urlSeparator}limit=${limit}&skip=0&${searchFieldName}=` +
                                    encodeURIComponent(refinedTerm);

                    if (componentDef.filter) {
                        const evaluateTemplate = function (template, data) {
                            return template.replace(/\{\{(.*?)\}\}/g, (match, expr) => {
                                try {
                                    let value = new Function('data', `return ${expr}`)(data);
                                    return value !== undefined ? value : match;
                                } catch (e) {
                                    return match;
                                }
                            });
                        };
                        try {
                            const queryString = evaluateTemplate(componentDef.filter, componentContext.submission.data);
                            retryUrl += '&' + queryString;
                        } catch (e) {
                            console.warn(`Error applying filter for toponimo retry:`, e);
                        }
                    }

                    try {
                        const retryResponse = await fetch(retryUrl, { headers: formattedHeaders, method: "GET" });
                        const retryData = await retryResponse.json();

                        let retryApiData;
                        if (componentDef.selectValues && retryData[componentDef.selectValues]) {
                            retryApiData = retryData[componentDef.selectValues];
                        } else if (Array.isArray(retryData)) {
                            retryApiData = retryData;
                        } else {
                            retryApiData = retryData.results || retryData.data || retryData.items || [];
                        }

                        if (retryApiData && Array.isArray(retryApiData) && retryApiData.length > 0) {
                            apiData = retryApiData;
                            console.log(`Toponimo retry returned ${retryApiData.length} results`);
                        }
                    } catch (retryError) {
                        console.error(`Error retrying without toponimo:`, retryError);
                    }
                }
            }

            if (apiData && Array.isArray(apiData) && apiData.length > 0) {
                let selectedItem = null;

                // First, try to find exact match based on the search term
                selectedItem = findExactMatch(apiData, searchTerm);

                if (selectedItem) {
                    console.log(`Found exact match for "${searchTerm}" in ${key}`);
                }

                // Try template-based matching using the component's display template
                // e.g., "<span>{{ item.TOP_DESCRIZIONE }} {{ item.VIE_DESCRIZIONE }}</span>"
                if (!selectedItem && componentDef.template) {
                    const templateFieldRegex = /\{\{\s*item\.(\w+)\s*\}\}/g;
                    const templateFields = [];
                    let tmplMatch;
                    while ((tmplMatch = templateFieldRegex.exec(componentDef.template)) !== null) {
                        templateFields.push(tmplMatch[1]);
                    }
                    if (templateFields.length > 0) {
                        const searchLower = String(searchTerm).toLowerCase().trim();
                        selectedItem = apiData.find(item => {
                            const displayString = templateFields.map(f => String(item[f] || '')).join(' ').trim().toLowerCase();
                            return displayString === searchLower;
                        });
                        if (selectedItem) {
                            console.log(`Found template-based match for "${searchTerm}" in ${key}`);
                        }
                    }
                }

                if (!selectedItem && typeof value === 'object' && value !== null) {
                    // Try to find exact match by common ID fields
                    const idFields = ['id', 'item_key', 'codice_catastale', 'value', 'code'];
                    for (const field of idFields) {
                        if (value[field]) {
                            const match = apiData.find(item => item[field] === value[field]);
                            if (match) {
                                selectedItem = match;
                                console.log(`Found exact match by ${field} for ${key}`);
                                break;
                            }
                        }
                    }
                }

                // If still no match, default to first result
                if (!selectedItem) {
                    selectedItem = apiData[0];
                    console.warn(`No exact match found for ${key}, using first result`);
                }

                // Set the full object using FormIO's setValue to trigger events
                formioComponent.setValue(selectedItem);
                console.log(`Set ${key} with full API result:`, selectedItem);
            } else {
                console.warn(`No API results found for ${key} with search term: "${searchTerm}"`);
                // Fallback: set the raw value we received (unless select has subfield template)
                if (PrefillService.SKIP_RAW_FILL_SELECT_WITH_SUBFIELDS && PrefillService._selectHasSubfieldTemplate(componentDef)) {
                    console.log(`Skipping raw fill for ${key}: select has subfield template, no API results`);
                } else {
                    formioComponent.setValue(value);
                }
            }
        } catch (apiError) {
            console.error(`Error fetching API data for ${key}:`, apiError);
            // Fallback: set the raw value (unless select has subfield template)
            if (PrefillService.SKIP_RAW_FILL_SELECT_WITH_SUBFIELDS && PrefillService._selectHasSubfieldTemplate(componentDef)) {
                console.log(`Skipping raw fill for ${key}: select has subfield template, API error`);
            } else {
                formioComponent.setValue(value);
            }
        }
    },

    /**
     * Handle province fields - map between province code and name
     * @private
     */
    async _handleProvince(key, value, formioComponent) {
        console.log(`${key} is a province field with value:`, value);

        if (!value || (typeof value !== 'string' && typeof value !== 'object')) {
            console.warn(`Invalid province value for ${key}:`, value);
            formioComponent.setValue(value);
            return;
        }

        let searchValue = typeof value === 'string' ? value.trim() :
                         (value.label || value.value || value.name || '').trim();

        if (!searchValue) {
            console.warn(`No searchable province value for ${key}:`, value);
            formioComponent.setValue(value);
            return;
        }

        // Helper function to calculate string similarity (Levenshtein-like)
        const similarity = (s1, s2) => {
            const longer = s1.length > s2.length ? s1 : s2;
            const shorter = s1.length > s2.length ? s2 : s1;
            const longerLength = longer.length;
            if (longerLength === 0) return 1.0;

            const editDistance = (s1, s2) => {
                s1 = s1.toLowerCase();
                s2 = s2.toLowerCase();
                const costs = [];
                for (let i = 0; i <= s1.length; i++) {
                    let lastValue = i;
                    for (let j = 0; j <= s2.length; j++) {
                        if (i === 0) {
                            costs[j] = j;
                        } else if (j > 0) {
                            let newValue = costs[j - 1];
                            if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
                                newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
                            }
                            costs[j - 1] = lastValue;
                            lastValue = newValue;
                        }
                    }
                    if (i > 0) costs[s2.length] = lastValue;
                }
                return costs[s2.length];
            };

            return (longerLength - editDistance(longer, shorter)) / longerLength;
        };

        let matchedValue = null;
        const searchLower = searchValue.toLowerCase();

        // Check if it's a province code (2 letters uppercase)
        if (searchValue.length === 2 && searchValue === searchValue.toUpperCase()) {
            // It's likely a code, try to find the name
            const provinceName = PrefillService.ITALIAN_PROVINCES[searchValue];
            if (provinceName) {
                console.log(`Matched province code ${searchValue} to name: ${provinceName}`);
                matchedValue = provinceName;
            }
        }
        // Check if it matches a province name
        else {
            // First try exact match (case-insensitive)
            let matchedCode = Object.entries(PrefillService.ITALIAN_PROVINCES).find(
                ([code, name]) => name.toLowerCase() === searchLower
            );

            if (matchedCode) {
                console.log(`Exact match found: ${searchValue} => ${matchedCode[0]}`);
                matchedValue = matchedCode[0]; // Use the code
            } else {
                // Try partial match (contains)
                matchedCode = Object.entries(PrefillService.ITALIAN_PROVINCES).find(
                    ([code, name]) => name.toLowerCase().includes(searchLower) || searchLower.includes(name.toLowerCase())
                );

                if (matchedCode) {
                    console.log(`Partial match found: ${searchValue} => ${matchedCode[0]}`);
                    matchedValue = matchedCode[0]; // Use the code
                } else {
                    // Use similarity matching to find the best match
                    let bestMatch = null;
                    let bestScore = 0;

                    Object.entries(PrefillService.ITALIAN_PROVINCES).forEach(([code, name]) => {
                        const score = similarity(searchValue, name);
                        if (score > bestScore) {
                            bestScore = score;
                            bestMatch = [code, name];
                        }
                    });

                    // Only use similarity match if confidence is high enough (> 0.6)
                    if (bestMatch && bestScore > 0.6) {
                        console.log(`Similarity match found: ${searchValue} => ${bestMatch[0]} (${bestMatch[1]}) with score ${bestScore.toFixed(2)}`);
                        matchedValue = bestMatch[0]; // Use the code
                    } else {
                        console.warn(`No good match found for province: ${searchValue}`);
                    }
                }
            }
        }

        // Set the matched value or fallback to original
        if (matchedValue) {
            formioComponent.setValue(matchedValue);
            console.log(`Set ${key} with matched province value:`, matchedValue);
        } else {
            console.warn(`Using original value for ${key}:`, value);
            formioComponent.setValue(value);
        }
    },

    /**
     * Handle day/date components - convert date formats
     * @private
     */
    async _handleDayComponent(key, value, componentDef, formioComponent) {
        console.log(`${key} is a day/date component with value:`, value);

        if (!value || typeof value !== 'string') {
            console.warn(`Invalid date value for ${key}:`, value);
            formioComponent.setValue(value);
            return;
        }

        // Clean the value - remove trailing slashes or other artifacts
        let cleanValue = value.trim().replace(/\/+$/, '');

        // Try to parse different date formats
        let parsedDate = null;

        // Format 1: ISO format YYYY-MM-DD
        const isoMatch = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
            const [, year, month, day] = isoMatch;
            parsedDate = { year, month, day };
            console.log(`Parsed ISO date: ${year}-${month}-${day}`);
        }

        // Format 2: DD/MM/YYYY or DD-MM-YYYY
        if (!parsedDate) {
            const europeanMatch = cleanValue.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
            if (europeanMatch) {
                const [, day, month, year] = europeanMatch;
                parsedDate = { year, month, day };
                console.log(`Parsed European date: ${day}/${month}/${year}`);
            }
        }

        // Format 3: MM/DD/YYYY or MM-DD-YYYY (US format)
        if (!parsedDate) {
            const usMatch = cleanValue.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
            if (usMatch) {
                const [, month, day, year] = usMatch;
                parsedDate = { year, month, day };
                console.log(`Parsed US date: ${month}/${day}/${year}`);
            }
        }

        if (parsedDate) {
            // Check if component uses dayFirst (default for European format)
            const dayFirst = componentDef.dayFirst !== false; // Default to true

            let formattedDate;
            if (dayFirst) {
                // European format: DD/MM/YYYY
                formattedDate = `${parsedDate.day}/${parsedDate.month}/${parsedDate.year}`;
            } else {
                // US format: MM/DD/YYYY
                formattedDate = `${parsedDate.month}/${parsedDate.day}/${parsedDate.year}`;
            }

            console.log(`Setting ${key} with formatted date: ${formattedDate}`);
            formioComponent.setValue(formattedDate);
        } else {
            console.warn(`Could not parse date format for ${key}: ${value}`);
            // Try to set the original value, FormIO might handle it
            formioComponent.setValue(cleanValue);
        }
    }
};

export default PrefillService;
